import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DataTable } from '@/components/common/DataTable';
import { levelCapAPI, agencyAPI, carrierAPI, levelAPI, contractTypeAPI } from '@/lib/api';
import { isKRRole, formatDate, getErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Loader2,
  Shield,
  Edit,
  Trash2,
  Search,
  ChevronRight,
  Check,
  ChevronsUpDown
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function LevelCapsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingCaps, setLoadingCaps] = useState(false);
  
  // Reference data
  const [agencies, setAgencies] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  
  // Selected agency and its caps
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyCaps, setAgencyCaps] = useState([]);
  const [agencySearchOpen, setAgencySearchOpen] = useState(false);
  const [agencySearch, setAgencySearch] = useState('');
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [capToDelete, setCapToDelete] = useState(null);
  const [editingCap, setEditingCap] = useState(null);
  const [capSearch, setCapSearch] = useState('');
  const [capFilters, setCapFilters] = useState({});
  const [formData, setFormData] = useState({
    carrier_id: '',
    contract_type_id: '',
    max_level_id: '',
    status: 'active',
    effective_date: ''
  });

  const isKR = isKRRole(user);

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [agenciesRes, carriersRes, levelsRes, typesRes] = await Promise.all([
        agencyAPI.list(),
        carrierAPI.list(),
        levelAPI.list(),
        contractTypeAPI.list()
      ]);
      setAgencies(agenciesRes.data);
      setCarriers(carriersRes.data.filter(c => c.active));
      setLevels(levelsRes.data.filter(l => l.active));
      setContractTypes(typesRes.data.filter(t => t.active));
    } catch (error) {
      toast.error('Failed to load reference data');
    } finally {
      setLoading(false);
    }
  };

  // Load caps for selected agency
  const loadAgencyCaps = async (agencyId) => {
    if (!agencyId) {
      setAgencyCaps([]);
      return;
    }
    
    setLoadingCaps(true);
    try {
      const response = await levelCapAPI.list({ agency_id: agencyId });
      setAgencyCaps(response.data);
    } catch (error) {
      toast.error('Failed to load agency level caps');
      setAgencyCaps([]);
    } finally {
      setLoadingCaps(false);
    }
  };

  // When agency changes, load its caps
  useEffect(() => {
    if (selectedAgency) {
      loadAgencyCaps(selectedAgency.agency_id);
    } else {
      setAgencyCaps([]);
    }
  }, [selectedAgency]);

  // Filter agencies for search
  const filteredAgencies = useMemo(() => {
    if (!agencySearch) return agencies;
    const search = agencySearch.toLowerCase();
    return agencies.filter(a => 
      a.agency_name.toLowerCase().includes(search) ||
      a.agency_id.toLowerCase().includes(search)
    );
  }, [agencies, agencySearch]);

  // Filter levels by selected carrier
  const filteredLevels = useMemo(() => {
    if (!formData.carrier_id) return [];
    return levels
      .filter(l => l.carrier_id === formData.carrier_id)
      .sort((a, b) => b.level_rank - a.level_rank);
  }, [levels, formData.carrier_id]);

  // Filter contract types by selected carrier
  const filteredContractTypes = useMemo(() => {
    if (!formData.carrier_id) return [];
    return contractTypes.filter(ct => ct.carrier_id === formData.carrier_id);
  }, [contractTypes, formData.carrier_id]);

  const openAddDialog = () => {
    setEditingCap(null);
    setFormData({
      carrier_id: '',
      contract_type_id: '',
      max_level_id: '',
      status: 'active',
      effective_date: ''
    });
    setShowDialog(true);
  };

  const openEditDialog = (cap) => {
    setEditingCap(cap);
    // Find the level_id that matches the max_level_rank
    const matchingLevel = levels.find(l => 
      l.carrier_id === cap.carrier_id && l.level_rank === cap.max_level_rank
    );
    setFormData({
      carrier_id: cap.carrier_id,
      contract_type_id: cap.contract_type_id || '',
      max_level_id: matchingLevel?.level_id || '',
      status: cap.status,
      effective_date: cap.effective_date || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.carrier_id || !formData.max_level_id) {
      toast.error('Please select a carrier and level');
      return;
    }
    
    const selectedLevel = levels.find(l => l.level_id === formData.max_level_id);
    if (!selectedLevel) {
      toast.error('Invalid level selected');
      return;
    }

    try {
      if (editingCap) {
        await levelCapAPI.update(editingCap.id, {
          max_level_rank: selectedLevel.level_rank,
          status: formData.status,
          effective_date: formData.effective_date || null
        });
        toast.success('Level cap updated');
      } else {
        await levelCapAPI.create({
          agency_id: selectedAgency.agency_id,
          carrier_id: formData.carrier_id,
          contract_type_id: formData.contract_type_id || null,
          max_level_rank: selectedLevel.level_rank,
          effective_date: formData.effective_date || null
        });
        toast.success('Level cap created');
      }
      setShowDialog(false);
      loadAgencyCaps(selectedAgency.agency_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save level cap'));
    }
  };

  const handleDelete = async () => {
    if (!capToDelete) return;
    try {
      await levelCapAPI.delete(capToDelete.id);
      toast.success('Level cap deleted');
      setShowDeleteDialog(false);
      setCapToDelete(null);
      loadAgencyCaps(selectedAgency.agency_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete level cap'));
    }
  };

  // Enrich caps with names
  const enrichedCaps = useMemo(() => {
    return agencyCaps.map(cap => {
      const carrier = carriers.find(c => c.carrier_id === cap.carrier_id);
      const contractType = contractTypes.find(ct => ct.contract_type_id === cap.contract_type_id);
      const level = levels.find(l => 
        l.carrier_id === cap.carrier_id && l.level_rank === cap.max_level_rank
      );
      
      return {
        ...cap,
        carrier_name: carrier?.carrier_name || 'Unknown',
        contract_type_name: contractType?.contract_type_name || null,
        max_level_name: level?.level_name || `Rank ${cap.max_level_rank}`
      };
    });
  }, [agencyCaps, carriers, contractTypes, levels]);

  // Filter definitions for caps table
  const capFilterDefs = useMemo(() => {
    const carrierOpts = [...new Set(enrichedCaps.map(c => c.carrier_name).filter(Boolean))].sort()
      .map(c => ({ value: c, label: c }));
    return [
      { key: 'carrier_name', label: 'Carrier', options: carrierOpts },
      { key: 'status', label: 'Status', options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ]}
    ];
  }, [enrichedCaps]);

  const capFilterFn = useCallback((row, fv) => {
    if (fv.carrier_name && fv.carrier_name !== 'all' && row.carrier_name !== fv.carrier_name) return false;
    if (fv.status && fv.status !== 'all' && row.status !== fv.status) return false;
    return true;
  }, []);

  const columns = [
    { 
      header: 'Carrier', accessor: 'carrier_name', sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-900 text-white rounded flex items-center justify-center text-sm font-semibold">
            {row.carrier_name?.charAt(0) || '?'}
          </div>
          <span className="font-medium">{row.carrier_name}</span>
        </div>
      )
    },
    { 
      header: 'Contract Type', accessor: 'contract_type_name', sortable: true,
      cell: (row) => row.contract_type_name || <span className="text-slate-400">All Types</span>
    },
    { 
      header: 'Max Level', accessor: 'max_level_name', sortable: true,
      cell: (row) => (
        <Badge variant="outline" className="font-medium">
          {row.max_level_name}
        </Badge>
      )
    },
    { 
      header: 'Status', accessor: 'status', sortable: true,
      cell: (row) => <StatusBadge status={row.status} />
    },
    { 
      header: 'Effective Date', accessor: 'effective_date', sortable: true,
      cell: (row) => formatDate(row.effective_date) || '-' 
    },
    ...(isKR ? [{
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEditDialog(row)} data-testid={`edit-cap-${row.id}`}>
            <Edit size={14} />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-500 hover:text-red-700"
            onClick={() => {
              setCapToDelete(row);
              setShowDeleteDialog(true);
            }}
            data-testid={`delete-cap-${row.id}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      )
    }] : [])
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="level-caps-page">
      <PageHeader 
        title="Agency Level Caps"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Level Caps', href: '/level-caps' }
        ]}
      />
      
      <div className="page-content">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Panel - Agency Selector */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Building2 size={18} className="text-slate-400" />
                  Select Agency
                </CardTitle>
                <CardDescription>
                  Choose an agency to view and manage their level caps
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Agency Search Combobox */}
                <Popover open={agencySearchOpen} onOpenChange={setAgencySearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={agencySearchOpen}
                      className="w-full justify-between"
                      data-testid="agency-selector"
                    >
                      {selectedAgency 
                        ? selectedAgency.agency_name 
                        : "Search agencies..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Search agencies..." 
                        value={agencySearch}
                        onValueChange={setAgencySearch}
                      />
                      <CommandList>
                        <CommandEmpty>No agency found.</CommandEmpty>
                        <CommandGroup>
                          <ScrollArea className="h-60">
                            {filteredAgencies.map((agency) => (
                              <CommandItem
                                key={agency.agency_id}
                                value={agency.agency_id}
                                onSelect={() => {
                                  setSelectedAgency(agency);
                                  setAgencySearchOpen(false);
                                  setAgencySearch('');
                                }}
                                className="cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedAgency?.agency_id === agency.agency_id 
                                      ? "opacity-100" 
                                      : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{agency.agency_name}</span>
                                  <span className="text-xs text-slate-500">{agency.agency_id.slice(0, 8)}...</span>
                                </div>
                              </CommandItem>
                            ))}
                          </ScrollArea>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Quick stats for selected agency */}
                {selectedAgency && (
                  <div className="pt-3 border-t space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Total Caps</span>
                      <Badge variant="secondary">{agencyCaps.length}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Active Caps</span>
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                        {agencyCaps.filter(c => c.status === 'active').length}
                      </Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Level Caps Table */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Shield size={18} className="text-slate-400" />
                    {selectedAgency ? `Level Caps: ${selectedAgency.agency_name}` : 'Level Caps'}
                  </CardTitle>
                  {selectedAgency && (
                    <CardDescription>
                      Manage maximum contract levels this agency can request per carrier
                    </CardDescription>
                  )}
                </div>
                {isKR && selectedAgency && (
                  <Button onClick={openAddDialog} data-testid="add-cap-btn">
                    <Plus size={16} className="mr-2" />
                    Add Cap
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {!selectedAgency ? (
                  <div className="text-center py-16 text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">No agency selected</p>
                    <p className="text-sm">Select an agency from the left panel to view their level caps</p>
                  </div>
                ) : loadingCaps ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={enrichedCaps}
                    emptyTitle="No level caps"
                    emptyDescription={
                      isKR 
                        ? "Add a level cap to define maximum contract levels for this agency."
                        : "No level caps have been configured for this agency."
                    }
                    searchValue={capSearch}
                    onSearchChange={setCapSearch}
                    searchPlaceholder="Search by carrier, level, type..."
                    searchFields={['carrier_name', 'contract_type_name', 'max_level_name']}
                    filters={capFilterDefs}
                    filterValues={capFilters}
                    onFilterChange={(key, val) => setCapFilters(prev => ({...prev, [key]: val}))}
                    filterFn={capFilterFn}
                    defaultSortKey="carrier_name"
                    defaultSortDir="asc"
                    defaultPageSize={25}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCap ? 'Edit Level Cap' : 'Add Level Cap'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Carrier *</Label>
                <Select
                  value={formData.carrier_id}
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    carrier_id: value,
                    contract_type_id: '',
                    max_level_id: ''
                  }))}
                  disabled={!!editingCap}
                >
                  <SelectTrigger data-testid="cap-carrier-select">
                    <SelectValue placeholder="Select carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {carriers.map(c => (
                      <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingCap && (
                  <p className="text-xs text-slate-500">Carrier cannot be changed. Delete and create new if needed.</p>
                )}
              </div>

              {formData.carrier_id && filteredContractTypes.length > 0 && !editingCap && (
                <div className="space-y-2">
                  <Label>Contract Type (Optional)</Label>
                  <Select
                    value={formData.contract_type_id || '__all__'}
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      contract_type_id: value === '__all__' ? '' : value 
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All types</SelectItem>
                      {filteredContractTypes.map(ct => (
                        <SelectItem key={ct.contract_type_id} value={ct.contract_type_id}>
                          {ct.contract_type_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Leave empty to apply cap to all contract types for this carrier.</p>
                </div>
              )}

              {formData.carrier_id && (
                <div className="space-y-2">
                  <Label>Maximum Level *</Label>
                  <Select
                    value={formData.max_level_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, max_level_id: value }))}
                  >
                    <SelectTrigger data-testid="cap-level-select">
                      <SelectValue placeholder="Select max level" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredLevels.map(l => (
                        <SelectItem key={l.level_id} value={l.level_id}>{l.level_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Agency can only request contracts up to this level.</p>
                </div>
              )}

              {editingCap && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Effective Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-cap-btn">
                {editingCap ? 'Update Cap' : 'Create Cap'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Level Cap</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this level cap?
              <span className="block mt-2 text-slate-600">
                Carrier: <strong>{capToDelete?.carrier_name}</strong><br/>
                Max Level: <strong>{capToDelete?.max_level_name}</strong>
              </span>
              <span className="block mt-2 text-amber-600">
                This agency will only be able to request Street Level contracts for this carrier after deletion.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
